/**
 * The /admin/access surface for users who are NOT grantors/admins (D12).
 * AssignmentsListPage's power tools (lookup any user, scope tree, break-
 * glass) are gated to reviewers server-side; a plain member gets this one
 * self-service card instead — file a delegation request naming yourself as
 * owner, then watch it move from pending to a decision.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { ResolvedId } from '../nameResolution';
import { DelegateAccessRequestDialog } from './DelegateAccessRequestDialog';
import { useAccessRequests } from './hooks';
function formatTimestamp(ts) {
    if (!ts || Number(ts.seconds) === 0) return '—';
    return new Date(Number(ts.seconds) * 1000).toLocaleDateString();
}
function statusVariant(status) {
    if (status === 'approved') return 'default';
    if (status === 'denied') return 'destructive';
    if (status === 'pending') return 'secondary';
    return 'outline';
}
export function DelegateMyAccessPanel() {
    const [open, setOpen] = useState(false);
    const { data, refetch } = useAccessRequests('');
    const requests = (data?.requests ?? []).filter((r)=>r.intent === 'delegation');
    return /*#__PURE__*/ _jsxs("div", {
        className: "container mx-auto p-6 space-y-6",
        "data-testid": Testids.adminAccess.delegateMyAccessPage,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "text-2xl font-semibold",
                                children: "Delegate my access"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground text-sm",
                                children: "Cover your access while you’re out — a reviewer approves before anything mints."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        "data-testid": Testids.adminAccess.delegateMyAccessButton,
                        onClick: ()=>setOpen(true),
                        children: [
                            /*#__PURE__*/ _jsx(UserPlus, {
                                className: "mr-2 h-4 w-4"
                            }),
                            "Delegate my access"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Card, {
                children: [
                    /*#__PURE__*/ _jsx(CardHeader, {
                        children: /*#__PURE__*/ _jsx(CardTitle, {
                            children: "Your delegation requests"
                        })
                    }),
                    /*#__PURE__*/ _jsx(CardContent, {
                        children: requests.length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-muted-foreground text-sm",
                            children: "No delegation requests filed yet."
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-3",
                            children: requests.map((r)=>/*#__PURE__*/ _jsxs("li", {
                                    "data-testid": Testids.adminAccess.delegateRequestRow,
                                    "data-request-id": r.id,
                                    className: "rounded-md border px-3 py-2 text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between gap-3",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        "Delegating to",
                                                        ' ',
                                                        /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: r.delegateUserId,
                                                            name: r.delegateDisplayName,
                                                            className: "font-medium"
                                                        }),
                                                        ' ',
                                                        "for ",
                                                        r.targetObjectType,
                                                        "/",
                                                        /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: r.targetObjectId,
                                                            name: r.targetDisplayName
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(Badge, {
                                                    variant: statusVariant(r.status),
                                                    "data-testid": Testids.adminAccess.delegateRequestStatus,
                                                    children: r.status
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-muted-foreground mt-1 text-xs",
                                            children: [
                                                formatTimestamp(r.effectiveStart),
                                                " → ",
                                                formatTimestamp(r.effectiveEnd)
                                            ]
                                        }),
                                        r.status === 'denied' && r.decisionReason && /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 text-xs text-destructive",
                                            children: r.decisionReason
                                        })
                                    ]
                                }, r.id))
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DelegateAccessRequestDialog, {
                open: open,
                onOpenChange: setOpen,
                onCreated: ()=>void refetch()
            })
        ]
    });
}
